import * as React from "react";
import { SGBSBadgeType, SGBSButtonType, SGBSColor, SGBSSize } from "../../common/common.typings";
import { isStringEmpty } from "../../common/stringUtils";

export interface BadgeProps {
  /**
   * The id of the current badge. Will be returned by the onClose function
   */
  id?: string;

  /**
   * The badge text to display will be overridden if children is defined,
   * Will be returned by the onClose function if the id is not defined.
   */
  text?: string;

  /**
   * The badge text size, (small or large)
   * default none
   */
  textSize?: "small" | "large";

  /**
   * this dictates the color of component (ie: primary, secondary, warning, danger, success, information, light, info, flat)
   * or any other predefined sgb4 color
   * default primary
   */
  color?: SGBSColor;

  /**
   * when true  pill class is applied
   * default false
   */
  pill?: boolean;

  /**
   * when true badge-prepend-square is applied
   * default false
   */
  square?: boolean;

  /**
   * When true will display the icon and emit the onClose if provided.
   * default false
   */
  closable?: boolean;

  /**
   * Type of the badge can be one of "default" | "outline" | "discreet" | "flat"
   * will be translated to  badge-default, badge-outline, badge-discreet,...
   * default value is null and will not not be appended
   */
  badgeType?: SGBSBadgeType;

  /**
   * additional classnames
   */
  className?: string;

  /**
   * size of the badge
   * default md
   */
  size?: SGBSSize;

  /**
   * close icon
   * default close
   */
  closeIcon?: "close" | "add" | string;

  /**
   * if closable is true, and when users click on it, the onClose will be fired
   * with the parameters id or text
   */
  onClose?: (id?: string) => void;
}

const buildBadgeClasses = (color: SGBSColor, type?: SGBSButtonType): string => {
  if (!type) {
    return `badge-${color}`;
  }
  if (type === "default") {
    return "sgbs-badge-default";
  }
  return `badge-${type}-${color}`;
};

const Badge: React.FC<React.PropsWithChildren<BadgeProps>> = ({
  id,
  text,
  textSize,
  badgeType,
  size = "md",
  color = "primary",
  closeIcon = "close",
  pill = false,
  square = false,
  closable = false,
  className = "",
  children,
  onClose,
}: React.PropsWithChildren<BadgeProps>) => {
  const badgeClass = buildBadgeClasses(color, badgeType);
  const prependClass = square ? "badge-prepend-square" : "";
  const pillClass = pill ? "badge-pill" : "";
  const textSizeClass = textSize ? `text-${textSize}` : "";

  const handleOnClose = (): void => {
    onClose?.(id ?? text);
  };

  const hasContent = !!children || !isStringEmpty(text);

  return (
    <span className={`badge ${badgeClass} badge-${size} ${pillClass} ${prependClass} ${textSizeClass} ${className}`}>
      {closable && (
        <em
          className={`icon ${hasContent ? "mr-1" : ""} ${onClose ? "action" : ""}`}
          style={onClose ? { cursor: "pointer" } : {}}
          onClick={handleOnClose}
        >
          {closeIcon}
        </em>
      )}
      {children ? children : text}
    </span>
  );
};

export default Badge;
